import { Fragment, useEffect, useMemo, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { Navigate } from "react-router-dom";
import { useDotBotsAuth } from "@dotbots-boutique/auth-sdk/react";
import { useApi } from "../lib/api";
import { hasPermission } from "../lib/permissions";
import { useI18n, useT } from "../i18n/I18nProvider";
import { ConfirmDeleteButton } from "../components/shared/ConfirmDeleteButton";
import { CopyButton } from "../components/shared/CopyButton";
import type { Agent, SlaPolicy, Team } from "@shared/types";

interface SyncResponse {
  ok: boolean;
  source?: "platform" | "jwt";
  teamCount?: number;
}

type AdminTab = "teams" | "agents" | "sla" | "private" | "forms";

interface IntakeForm {
  id: string;
  name: string;
  slug: string;
  team_mode: "fixed" | "choose";
  fixed_team_id: string | null;
  selectable_team_ids: string[];
  // Public-facing heading per language; the admin-only `name` above
  // stays as-is so operators can keep an internal label.
  title_i18n: Record<string, string>;
  intro_i18n: Record<string, string>;
  success_i18n: Record<string, string>;
  // Per-language overrides for the visitor-facing field labels and
  // submit caption. Shape: { lang: { fieldKey: value } } — empty
  // entries fall back to the AI-translated defaults.
  labels_i18n: Record<string, Record<string, string>>;
  // Per-language redirect URL shown after a successful submission.
  // Empty entries fall through to the regular thanks-text behaviour.
  redirect_i18n: Record<string, string>;
  // Per-language HTML small print rendered under the form.
  note_i18n: Record<string, string>;
  // Per-language fixed subject. When non-empty for the active
  // language the public form hides the subject input.
  subject_i18n: Record<string, string>;
}

// Keys an operator can override per form per language. Matches the
// backend's LABEL_OVERRIDE_KEYS — only these slots are persisted; any
// other key the admin UI might accidentally include is dropped by the
// Zod schema on the backend.
const LABEL_OVERRIDE_KEYS = [
  "name",
  "email",
  "subject",
  "description",
  "team",
  "teamPlaceholder",
  "submit",
] as const;
type LabelOverrideKey = typeof LABEL_OVERRIDE_KEYS[number];
type LabelsI18n = Record<string, Partial<Record<LabelOverrideKey, string>>>;

interface PrivateTicketRow {
  id: string;
  ticket_number: number;
  status: string;
  priority: string;
  assignee_id: string | null;
  assignee_name: string | null;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
}

export default function Admin() {
  const api = useApi();
  const t = useT();
  const { user } = useDotBotsAuth();
  const roles = user?.roles ?? [];
  // Admin page is admin-only. agents.write is unique to the admin role,
  // so it doubles as the gate for accessing this page at all.
  const canWrite = hasPermission(roles, "agents.write");
  const canRead = canWrite;

  const [tab, setTab] = useState<AdminTab>("teams");
  const [teams, setTeams] = useState<Team[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [slaPolicies, setSlaPolicies] = useState<SlaPolicy[]>([]);
  const [message, setMessage] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [syncing, setSyncing] = useState(false);

  const loadAll = async () => {
    const [tm, ag, sl] = await Promise.all([
      api.get<{ data: Team[] }>("/api/teams"),
      api.get<{ data: Agent[] }>("/api/agents"),
      api.get<{ data: SlaPolicy[] }>("/api/sla-policies"),
    ]);
    setTeams(tm.data);
    setAgents(ag.data);
    setSlaPolicies(sl.data);
  };

  useEffect(() => {
    if (!canRead) return;
    loadAll().catch((err) => setMessage({ kind: "error", text: (err as Error).message }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead]);

  const triggerSync = async () => {
    setSyncing(true);
    setMessage(null);
    try {
      const res = await api.post<SyncResponse>("/api/me/sync");
      if (res.source === "platform") {
        setMessage({ kind: "ok", text: t("admin.syncSuccess", { count: res.teamCount ?? 0 }) });
      } else {
        setMessage({ kind: "ok", text: t("admin.syncSuccessJwt") });
      }
      await loadAll();
    } catch (err) {
      setMessage({ kind: "error", text: (err as Error).message });
    } finally {
      setSyncing(false);
    }
  };

  // Non-admin lands here only by typing the URL or following a stale
  // bookmark — bounce them to the ticket list instead of showing an
  // empty page.
  if (!canRead) {
    return <Navigate to="/tickets" replace />;
  }

  const handleError = (err: unknown) => setMessage({ kind: "error", text: (err as Error).message });

  return (
    <>
      <h2>{t("admin.title")}</h2>
      <p style={{ color: "var(--text-muted)" }}>{t("admin.intro")}</p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>{t("admin.sync")}</h3>
        <p style={{ color: "var(--text-muted)" }}>{t("admin.syncIntro")}</p>
        <button className="primary" onClick={triggerSync} disabled={syncing || !canWrite}>
          {syncing ? t("admin.syncing") : t("admin.sync")}
        </button>
      </div>

      {message && (
        <div className="card" style={{ color: message.kind === "ok" ? "var(--ok)" : "var(--danger)" }}>
          {message.text}
        </div>
      )}

      <div className="admin-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "teams"}
          className={tab === "teams" ? "active" : ""}
          onClick={() => setTab("teams")}
        >
          {t("admin.teams.heading")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "agents"}
          className={tab === "agents" ? "active" : ""}
          onClick={() => setTab("agents")}
        >
          {t("admin.agents.heading")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "sla"}
          className={tab === "sla" ? "active" : ""}
          onClick={() => setTab("sla")}
        >
          {t("admin.sla.heading")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "private"}
          className={tab === "private" ? "active" : ""}
          onClick={() => setTab("private")}
        >
          {t("admin.private.heading")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "forms"}
          className={tab === "forms" ? "active" : ""}
          onClick={() => setTab("forms")}
        >
          {t("admin.forms.heading")}
        </button>
      </div>

      {tab === "teams" && (
        <TeamsCard
          teams={teams}
          slaPolicies={slaPolicies}
          canWrite={canWrite}
          onChange={loadAll}
          onError={handleError}
        />
      )}

      {tab === "agents" && (
        <AgentsCard
          agents={agents}
          teams={teams}
          canWrite={canWrite}
          onChange={loadAll}
          onError={handleError}
        />
      )}

      {tab === "sla" && (
        <SlaCard
          slaPolicies={slaPolicies}
          canWrite={canWrite}
          onChange={loadAll}
          onError={handleError}
        />
      )}

      {tab === "private" && (
        <>
          <BulkReassignCard
            agents={agents}
            canWrite={canWrite}
            onError={handleError}
            onDone={(count) => setMessage({ kind: "ok", text: t("admin.bulk.success", { n: count }) })}
          />
          <PrivateTicketsCard
            agents={agents}
            canWrite={canWrite}
            onError={handleError}
          />
        </>
      )}

      {tab === "forms" && (
        <IntakeFormsCard
          teams={teams}
          canWrite={canWrite}
          onError={handleError}
        />
      )}
    </>
  );
}

interface CardProps {
  canWrite: boolean;
  onChange: () => Promise<void>;
  onError: (err: unknown) => void;
}

function PlatformBadge({ origin }: { origin: "platform" | "manual" }) {
  const t = useT();
  return (
    <span className="badge role-badge">
      {origin === "platform" ? t("admin.platformBadge") : t("admin.manualBadge")}
    </span>
  );
}

// ---------- Teams ----------

function TeamsCard({ teams, slaPolicies, canWrite, onChange, onError }: CardProps & { teams: Team[]; slaPolicies: SlaPolicy[] }) {
  const api = useApi();
  const t = useT();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [slaId, setSlaId] = useState("");

  const create = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/api/teams", { name: name.trim(), defaultSlaId: slaId || null });
      setCreating(false);
      setName("");
      setSlaId("");
      await onChange();
    } catch (err) {
      onError(err);
    }
  };

  return (
    <div className="card">
      <div className="toolbar">
        <h3 style={{ margin: 0 }}>{t("admin.teams.heading")}</h3>
        {canWrite && !creating && <button onClick={() => setCreating(true)}>{t("admin.teams.newButton")}</button>}
      </div>

      {creating && (
        <div style={{ display: "flex", gap: 8, alignItems: "end", marginBottom: 12, flexWrap: "wrap" }}>
          <div className="field" style={{ flex: 1, minWidth: 200, marginBottom: 0 }}>
            <label>{t("admin.teams.name")}</label>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field" style={{ minWidth: 200, marginBottom: 0 }}>
            <label>{t("admin.teams.defaultSla")}</label>
            <select value={slaId} onChange={(e) => setSlaId(e.target.value)}>
              <option value="">—</option>
              {slaPolicies.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <button className="primary" onClick={create} disabled={!name.trim()}>{t("common.create")}</button>
          <button onClick={() => { setCreating(false); setName(""); setSlaId(""); }}>{t("common.cancel")}</button>
        </div>
      )}

      <table className="admin-table">
        <thead>
          <tr>
            <th>{t("admin.teams.name")}</th>
            <th>{t("admin.teams.defaultSla")}</th>
            <th>{t("admin.teams.source")}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {teams.map((tm) => (
            <TeamRow
              key={tm.id}
              team={tm}
              slaPolicies={slaPolicies}
              canWrite={canWrite}
              onChange={onChange}
              onError={onError}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Normalise for comparison + storage. Same rule the backend expects
// (lower-cased, trimmed); dedup so two case-variants of the same
// address don't produce two rows.
function normaliseAliases(list: readonly string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of list) {
    const v = raw.trim().toLowerCase();
    if (!v || seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

// Chip-style editor for a list of email addresses. The list is the
// source of truth — the input row below only serves as a staging
// field for the next address to add. Enter and comma both commit;
// blank Backspace removes the last chip.
function EmailAliasList({
  value,
  onChange,
  disabled,
  placeholder,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const t = useT();
  const [draft, setDraft] = useState("");

  const commit = (raw: string) => {
    const cleaned = raw.trim().toLowerCase();
    if (!cleaned) return;
    if (value.some((a) => a.toLowerCase() === cleaned)) {
      setDraft("");
      return;
    }
    onChange([...value, cleaned]);
    setDraft("");
  };

  const remove = (index: number) => {
    const next = value.slice();
    next.splice(index, 1);
    onChange(next);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(draft);
    } else if (e.key === "Backspace" && draft === "" && value.length > 0) {
      e.preventDefault();
      remove(value.length - 1);
    }
  };

  return (
    <div className="email-alias-list">
      {value.length > 0 && (
        <ul className="email-alias-chips">
          {value.map((addr, i) => (
            <li key={`${addr}-${i}`} className="email-alias-chip">
              <span>{addr}</span>
              {!disabled && (
                <button
                  type="button"
                  className="email-alias-chip-x"
                  onClick={() => remove(i)}
                  aria-label={t("common.delete")}
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="email-alias-input-row">
        <input
          type="email"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => commit(draft)}
          placeholder={placeholder}
          disabled={disabled}
        />
        <button
          type="button"
          onClick={() => commit(draft)}
          disabled={disabled || !draft.trim()}
        >
          {t("common.add")}
        </button>
      </div>
    </div>
  );
}

function TeamRow({ team, slaPolicies, canWrite, onChange, onError }: CardProps & { team: Team; slaPolicies: SlaPolicy[] }) {
  const api = useApi();
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const [name, setName] = useState(team.name);
  const [slaId, setSlaId] = useState(team.default_sla_id ?? "");
  const [signature, setSignature] = useState(team.signature ?? "");
  const [aliases, setAliases] = useState<string[]>(normaliseAliases(team.email_aliases ?? []));

  const dirty =
    name !== team.name
    || (slaId || null) !== team.default_sla_id
    || (signature || null) !== (team.signature ?? null)
    || JSON.stringify(normaliseAliases(team.email_aliases ?? [])) !== JSON.stringify(aliases);

  const save = async () => {
    try {
      await api.patch(`/api/teams/${team.id}`, {
        name,
        defaultSlaId: slaId || null,
        signature: signature.trim() ? signature : null,
        emailAliases: aliases,
      });
      await onChange();
    } catch (err) { onError(err); }
  };

  const remove = async () => {
    try {
      await api.del(`/api/teams/${team.id}`);
      await onChange();
    } catch (err) { onError(err); }
  };

  return (
    <>
      <tr>
        <td data-label={t("admin.teams.name")}><input value={name} onChange={(e) => setName(e.target.value)} disabled={!canWrite} /></td>
        <td data-label={t("admin.teams.defaultSla")}>
          <select value={slaId} onChange={(e) => setSlaId(e.target.value)} disabled={!canWrite}>
            <option value="">—</option>
            {slaPolicies.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </td>
        <td data-label={t("admin.teams.source")}><PlatformBadge origin={team.platform_team_id ? "platform" : "manual"} /></td>
        <td className="admin-actions">
          <button onClick={() => setExpanded(!expanded)}>
            {expanded ? "▾" : "▸"} {t("admin.detailsToggle")}
          </button>{" "}
          {dirty && canWrite && <button className="primary" onClick={save}>{t("common.save")}</button>}{" "}
          {canWrite && <ConfirmDeleteButton onDelete={remove} hint={t("admin.teams.deleteConfirm")} />}
        </td>
      </tr>
      {expanded && (
        <tr className="admin-row-expanded">
          <td colSpan={4} style={{ background: "var(--surface-alt)" }}>
            <div className="field">
              <label>{t("admin.signature")}</label>
              <textarea
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder={t("admin.signaturePlaceholder")}
                disabled={!canWrite}
              />
              <small style={{ color: "var(--text-muted)" }}>{t("admin.teamSignatureHelp")}</small>
            </div>
            <div className="field">
              <label>{t("admin.teamAliases")}</label>
              <EmailAliasList
                value={aliases}
                onChange={setAliases}
                disabled={!canWrite}
                placeholder="support@example.com"
              />
              <small style={{ color: "var(--text-muted)" }}>{t("admin.teamAliasesHelp")}</small>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ---------- Agents ----------

function AgentsCard({ agents, teams, canWrite, onChange, onError }: CardProps & { agents: Agent[]; teams: Team[] }) {
  const api = useApi();
  const t = useT();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [teamId, setTeamId] = useState("");

  const create = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/api/agents", {
        name: name.trim(),
        email: email.trim() || null,
        teamId: teamId || null,
      });
      setCreating(false);
      setName(""); setEmail(""); setTeamId("");
      await onChange();
    } catch (err) { onError(err); }
  };

  return (
    <div className="card">
      <div className="toolbar">
        <h3 style={{ margin: 0 }}>{t("admin.agents.heading")}</h3>
        {canWrite && !creating && <button onClick={() => setCreating(true)}>{t("admin.agents.newButton")}</button>}
      </div>

      {creating && (
        <div style={{ display: "flex", gap: 8, alignItems: "end", marginBottom: 12, flexWrap: "wrap" }}>
          <div className="field" style={{ flex: 1, minWidth: 160, marginBottom: 0 }}>
            <label>{t("contacts.tableName")}</label>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field" style={{ flex: 1, minWidth: 200, marginBottom: 0 }}>
            <label>{t("common.email")}</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="field" style={{ minWidth: 160, marginBottom: 0 }}>
            <label>{t("admin.agents.team")}</label>
            <select value={teamId} onChange={(e) => setTeamId(e.target.value)}>
              <option value="">—</option>
              {teams.map((tm) => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
            </select>
          </div>
          <button className="primary" onClick={create} disabled={!name.trim()}>{t("common.create")}</button>
          <button onClick={() => { setCreating(false); setName(""); setEmail(""); setTeamId(""); }}>{t("common.cancel")}</button>
        </div>
      )}

      <table className="admin-table">
        <thead>
          <tr>
            <th>{t("contacts.tableName")}</th>
            <th>{t("common.email")}</th>
            <th>{t("admin.agents.team")}</th>
            <th>{t("admin.teams.source")}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {agents.map((a) => (
            <AgentRow key={a.id} agent={a} teams={teams} canWrite={canWrite} onChange={onChange} onError={onError} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AgentRow({ agent, teams, canWrite, onChange, onError }: CardProps & { agent: Agent; teams: Team[] }) {
  const api = useApi();
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const [name, setName] = useState(agent.name);
  const [email, setEmail] = useState(agent.email ?? "");
  const [teamId, setTeamId] = useState(agent.team_id ?? "");
  const [signature, setSignature] = useState(agent.signature ?? "");
  const [aliases, setAliases] = useState<string[]>(normaliseAliases(agent.email_aliases ?? []));

  const dirty =
    name !== agent.name
    || email !== (agent.email ?? "")
    || (teamId || null) !== agent.team_id
    || (signature || null) !== (agent.signature ?? null)
    || JSON.stringify(normaliseAliases(agent.email_aliases ?? [])) !== JSON.stringify(aliases);

  const save = async () => {
    try {
      await api.patch(`/api/agents/${agent.id}`, {
        name,
        email: email || null,
        teamId: teamId || null,
        signature: signature.trim() ? signature : null,
        emailAliases: aliases,
      });
      await onChange();
    } catch (err) { onError(err); }
  };

  const remove = async () => {
    try {
      await api.del(`/api/agents/${agent.id}`);
      await onChange();
    } catch (err) { onError(err); }
  };

  return (
    <>
      <tr>
        <td data-label={t("contacts.tableName")}><input value={name} onChange={(e) => setName(e.target.value)} disabled={!canWrite} /></td>
        <td data-label={t("common.email")}><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={!canWrite} /></td>
        <td data-label={t("admin.agents.team")}>
          <select value={teamId} onChange={(e) => setTeamId(e.target.value)} disabled={!canWrite}>
            <option value="">—</option>
            {teams.map((tm) => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
          </select>
        </td>
        <td data-label={t("admin.teams.source")}><PlatformBadge origin={agent.platform_user_id ? "platform" : "manual"} /></td>
        <td className="admin-actions">
          <button onClick={() => setExpanded(!expanded)}>
            {expanded ? "▾" : "▸"} {t("admin.detailsToggle")}
          </button>{" "}
          {dirty && canWrite && <button className="primary" onClick={save}>{t("common.save")}</button>}{" "}
          {canWrite && <ConfirmDeleteButton onDelete={remove} hint={t("admin.agents.deleteConfirm")} />}
        </td>
      </tr>
      {expanded && (
        <tr className="admin-row-expanded">
          <td colSpan={5} style={{ background: "var(--surface-alt)" }}>
            <div className="field">
              <label>{t("admin.signature")}</label>
              <textarea
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder={t("admin.signaturePlaceholder")}
                disabled={!canWrite}
              />
              <small style={{ color: "var(--text-muted)" }}>{t("admin.userSignatureHelp")}</small>
            </div>
            <div className="field">
              <label>{t("admin.userAliases")}</label>
              <EmailAliasList
                value={aliases}
                onChange={setAliases}
                disabled={!canWrite}
                placeholder="lien.support@example.com"
              />
              <small style={{ color: "var(--text-muted)" }}>{t("admin.userAliasesHelp")}</small>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ---------- SLA policies ----------

function SlaCard({ slaPolicies, canWrite, onChange, onError }: CardProps & { slaPolicies: SlaPolicy[] }) {
  const api = useApi();
  const t = useT();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [first, setFirst] = useState("60");
  const [res, setRes] = useState("240");

  const create = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/api/sla-policies", {
        name: name.trim(),
        firstResponseMins: Number(first),
        resolutionMins: Number(res),
      });
      setCreating(false);
      setName(""); setFirst("60"); setRes("240");
      await onChange();
    } catch (err) { onError(err); }
  };

  return (
    <div className="card">
      <div className="toolbar">
        <h3 style={{ margin: 0 }}>{t("admin.sla.heading")}</h3>
        {canWrite && !creating && <button onClick={() => setCreating(true)}>{t("admin.sla.newButton")}</button>}
      </div>

      {creating && (
        <div style={{ display: "flex", gap: 8, alignItems: "end", marginBottom: 12, flexWrap: "wrap" }}>
          <div className="field" style={{ flex: 1, minWidth: 200, marginBottom: 0 }}>
            <label>{t("admin.teams.name")}</label>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field" style={{ width: 160, marginBottom: 0 }}>
            <label>{t("admin.sla.firstResponseMins")}</label>
            <input type="number" min={1} max={60 * 24 * 30} value={first} onChange={(e) => setFirst(e.target.value)} />
          </div>
          <div className="field" style={{ width: 160, marginBottom: 0 }}>
            <label>{t("admin.sla.resolutionMins")}</label>
            <input type="number" min={1} max={60 * 24 * 30} value={res} onChange={(e) => setRes(e.target.value)} />
          </div>
          <button className="primary" onClick={create} disabled={!name.trim()}>{t("common.create")}</button>
          <button onClick={() => { setCreating(false); setName(""); setFirst("60"); setRes("240"); }}>{t("common.cancel")}</button>
        </div>
      )}

      <table className="admin-table">
        <thead>
          <tr>
            <th>{t("admin.teams.name")}</th>
            <th>{t("admin.sla.firstResponseMins")}</th>
            <th>{t("admin.sla.resolutionMins")}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {slaPolicies.map((s) => (
            <SlaRow key={s.id} sla={s} canWrite={canWrite} onChange={onChange} onError={onError} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SlaRow({ sla, canWrite, onChange, onError }: CardProps & { sla: SlaPolicy }) {
  const api = useApi();
  const t = useT();
  const [name, setName] = useState(sla.name);
  const [first, setFirst] = useState(String(sla.first_response_mins));
  const [res, setRes] = useState(String(sla.resolution_mins));

  const dirty = name !== sla.name
    || Number(first) !== sla.first_response_mins
    || Number(res) !== sla.resolution_mins;

  const save = async () => {
    try {
      await api.patch(`/api/sla-policies/${sla.id}`, {
        name,
        firstResponseMins: Number(first),
        resolutionMins: Number(res),
      });
      await onChange();
    } catch (err) { onError(err); }
  };

  const remove = async () => {
    try {
      await api.del(`/api/sla-policies/${sla.id}`);
      await onChange();
    } catch (err) { onError(err); }
  };

  return (
    <tr>
      <td data-label={t("admin.teams.name")}><input value={name} onChange={(e) => setName(e.target.value)} disabled={!canWrite} /></td>
      <td data-label={t("admin.sla.firstResponseMins")}><input type="number" min={1} value={first} onChange={(e) => setFirst(e.target.value)} disabled={!canWrite} /></td>
      <td data-label={t("admin.sla.resolutionMins")}><input type="number" min={1} value={res} onChange={(e) => setRes(e.target.value)} disabled={!canWrite} /></td>
      <td className="admin-actions">
        {dirty && canWrite && <button className="primary" onClick={save}>{t("common.save")}</button>}{" "}
        {canWrite && <ConfirmDeleteButton onDelete={remove} hint={t("admin.sla.deleteConfirm")} />}
      </td>
    </tr>
  );
}

// ---------- Private tickets (admin reassignment) ----------

function PrivateTicketsCard({
  agents,
  canWrite,
  onError,
}: { agents: Agent[]; canWrite: boolean; onError: (err: unknown) => void }) {
  const api = useApi();
  const t = useT();
  const [rows, setRows] = useState<PrivateTicketRow[]>([]);
  const [loading, setLoading] = useState(false);
  // Per-row reassign state: ticket id → picked agent id
  const [reassignFor, setReassignFor] = useState<string | null>(null);
  const [pickedAgent, setPickedAgent] = useState<string>("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get<{ data: PrivateTicketRow[] }>("/api/tickets/private-summary");
      setRows(res.data);
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startReassign = (id: string, currentAssignee: string | null) => {
    setReassignFor(id);
    setPickedAgent(currentAssignee ?? "");
  };
  const cancelReassign = () => {
    setReassignFor(null);
    setPickedAgent("");
  };
  const submitReassign = async (ticketId: string) => {
    if (!pickedAgent) return;
    setSaving(true);
    try {
      await api.post(`/api/tickets/${ticketId}/reassign-private`, { assigneeId: pickedAgent });
      cancelReassign();
      await load();
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <div className="toolbar">
        <h3 style={{ margin: 0 }}>{t("admin.private.heading")}</h3>
        <button onClick={load} disabled={loading} title={t("common.refresh")}>
          ⟳ {t("common.refresh")}
        </button>
      </div>
      <p style={{ color: "var(--text-muted)", marginTop: 0 }}>
        {t("admin.private.intro")}
      </p>

      <table className="admin-table">
        <thead>
          <tr>
            <th>{t("ticket.number")}</th>
            <th>{t("ticket.status")}</th>
            <th>{t("ticket.priority")}</th>
            <th>{t("ticket.assignee")}</th>
            <th>{t("ticket.lastUpdate")}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((tk) => (
            <Fragment key={tk.id}>
              <tr>
                <td data-label={t("ticket.number")}>#{tk.ticket_number}</td>
                <td data-label={t("ticket.status")}>{t(`status.${tk.status}`)}</td>
                <td data-label={t("ticket.priority")}>{t(`priority.${tk.priority}`)}</td>
                <td data-label={t("ticket.assignee")}>{tk.assignee_name ?? "—"}</td>
                <td data-label={t("ticket.lastUpdate")}>
                  {new Date(tk.last_message_at ?? tk.updated_at).toLocaleString()}
                </td>
                <td className="admin-actions">
                  {canWrite && (
                    <button
                      onClick={() => reassignFor === tk.id ? cancelReassign() : startReassign(tk.id, tk.assignee_id)}
                    >
                      {reassignFor === tk.id ? t("common.cancel") : t("admin.private.reassign")}
                    </button>
                  )}
                </td>
              </tr>
              {/* Inline form on a second row so the picker + buttons get
                  the full table width instead of being squeezed into the
                  action cell. */}
              {reassignFor === tk.id && (
                <tr className="admin-row-expanded">
                  <td colSpan={6} style={{ background: "var(--surface-alt)" }}>
                    <div className="reassign-form">
                      <label>
                        {t("admin.private.pickAgent")}
                        <select
                          value={pickedAgent}
                          onChange={(e) => setPickedAgent(e.target.value)}
                          disabled={saving}
                        >
                          <option value="">{t("admin.private.pickAgent")}</option>
                          {agents.map((a) => (
                            <option key={a.id} value={a.id}>{a.name}</option>
                          ))}
                        </select>
                      </label>
                      <button
                        className="primary"
                        onClick={() => submitReassign(tk.id)}
                        disabled={saving || !pickedAgent || pickedAgent === tk.assignee_id}
                      >
                        {saving ? t("common.saving") : t("common.save")}
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
          {rows.length === 0 && !loading && (
            <tr>
              <td colSpan={6} style={{ color: "var(--text-muted)", textAlign: "center", padding: 12 }}>
                {t("admin.private.empty")}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Intake forms (admin) ----------

function defaultSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 60) || "form";
}

// Intake forms support any IETF language code as a key, so the
// per-language map is just `{[code: string]: string}` rather than a
// fixed enum. The curated COMMON_LANGS list below feeds the "Add
// language" picker on the editor; admins can also type custom codes
// (Mandarin "zh", Spanish "es", Portuguese-Brazil "pt-BR", …).
type I18nMap = Record<string, string>;
const LANG_CODE_RE = /^[a-z]{2,3}(?:-[a-z0-9]{2,8})?$/i;
const COMMON_LANGS = [
  "en", "nl", "fr", "de", "es", "pt", "it", "pl", "ru",
  "zh", "ja", "ko", "ar", "tr", "sv", "da", "no", "fi",
  "cs", "ro", "hu", "uk", "he", "hi", "th", "vi", "id", "el",
];

function langLabel(code: string, uiLang: string): string {
  try {
    const dn = new Intl.DisplayNames([uiLang], { type: "language" });
    const name = dn.of(code);
    if (name) return name;
  } catch { /* older runtime */ }
  return code;
}

// JSONB columns sometimes return as strings — accept either shape and
// normalise to a plain object so callers can use it directly.
function asI18nMap(raw: unknown): I18nMap {
  if (!raw) return {};
  if (typeof raw === "string") {
    try { return (JSON.parse(raw) ?? {}) as I18nMap; } catch { return {}; }
  }
  if (typeof raw === "object") return raw as I18nMap;
  return {};
}

interface FormDraft {
  name: string;
  slug: string;
  teamMode: "fixed" | "choose";
  fixedTeamId: string;
  selectableTeamIds: string[]; // empty = all teams in choose mode
  titleI18n: I18nMap;
  introI18n: I18nMap;
  successI18n: I18nMap;
  labelsI18n: LabelsI18n;
  redirectI18n: I18nMap;
  noteI18n: I18nMap;
  subjectI18n: I18nMap;
}

const EMPTY_FORM_DRAFT: FormDraft = {
  name: "",
  slug: "",
  teamMode: "fixed",
  fixedTeamId: "",
  selectableTeamIds: [],
  titleI18n: {},
  introI18n: {},
  successI18n: {},
  labelsI18n: {},
  redirectI18n: {},
  noteI18n: {},
  subjectI18n: {},
};

// Sample CSS shown in the restyle popup so an operator copy-pasting
// the embed snippet on their site has a concrete starting point for
// styling. Lists every overridable class once so the popup doubles
// as living documentation.
const EMBED_RESTYLE_SAMPLE_CSS = `.ds-form {
  font-family: 'Inter', system-ui, sans-serif;
  background: #f9fafb;
  padding: 24px;
  border-radius: 12px;
  gap: 16px;
}
.ds-title {
  color: #1e3a8a;
  font-size: 22px;
}
.ds-intro {
  color: #475569;
}
.ds-field {
  gap: 6px;
}
.ds-label {
  font-weight: 600;
  color: #334155;
}
.ds-input,
.ds-textarea {
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 10px 12px;
  background: #fff;
}
.ds-input:focus,
.ds-textarea:focus {
  outline: 2px solid #2563eb;
  outline-offset: -1px;
  border-color: #2563eb;
}
.ds-submit {
  background: #2563eb;
  color: #fff;
  border-radius: 999px;
  padding: 12px 22px;
  font-weight: 600;
}
.ds-submit:hover { background: #1d4ed8; }
.ds-error {
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
}
.ds-thanks {
  background: #ecfdf5;
  color: #065f46;
  border-radius: 12px;
  padding: 20px;
}
.ds-fineprint {
  color: #94a3b8;
  font-size: 11px;
}`;

const EMBED_OVERRIDABLE_CLASSES = [
  ".ds-form",
  ".ds-title",
  ".ds-intro",
  ".ds-field",
  ".ds-label",
  ".ds-input",
  ".ds-textarea",
  ".ds-submit",
  ".ds-error",
  ".ds-thanks",
  ".ds-fineprint",
];

function IntakeFormsCard({
  teams,
  canWrite,
  onError,
}: { teams: Team[]; canWrite: boolean; onError: (err: unknown) => void }) {
  const api = useApi();
  const t = useT();
  const { lang: uiLang } = useI18n();
  const [forms, setForms] = useState<IntakeForm[]>([]);
  const [editingId, setEditingId] = useState<string | "new" | null>(null);
  const [draft, setDraft] = useState<FormDraft>(EMPTY_FORM_DRAFT);
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [restyleOpen, setRestyleOpen] = useState(false);

  const publicBase = (() => {
    const env = import.meta.env.VITE_PUBLIC_BASE_URL ?? import.meta.env.VITE_APP_BASE_URL;
    if (typeof env === "string" && env.length > 0) return env.replace(/\/$/, "");
    return window.location.origin;
  })();

  const load = async () => {
    try {
      const res = await api.get<{ data: IntakeForm[] }>("/api/intake-forms");
      setForms(res.data);
    } catch (err) { onError(err); }
  };
  useEffect(() => { load().catch(() => {}); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const startNew = () => {
    setDraft(EMPTY_FORM_DRAFT);
    setEditingId("new");
    setSlugTouched(false);
  };
  const startEdit = (f: IntakeForm) => {
    // Some Postgres clients hand JSONB back as a string; parse defensively
    // so a serialised "{...}" still populates the editor.
    const parseJsonMap = (raw: unknown): I18nMap => {
      if (!raw) return {};
      if (typeof raw === "string") {
        try { return JSON.parse(raw) as I18nMap; } catch { return {}; }
      }
      if (typeof raw === "object") return raw as I18nMap;
      return {};
    };
    const parseLabelsMap = (raw: unknown): LabelsI18n => {
      const outer = parseJsonMap(raw) as unknown as Record<string, unknown>;
      const out: LabelsI18n = {};
      for (const [langCode, inner] of Object.entries(outer)) {
        if (!inner || typeof inner !== "object") continue;
        const cleaned: Partial<Record<LabelOverrideKey, string>> = {};
        for (const k of LABEL_OVERRIDE_KEYS) {
          const v = (inner as Record<string, unknown>)[k];
          if (typeof v === "string") cleaned[k] = v;
        }
        out[langCode] = cleaned;
      }
      return out;
    };
    setDraft({
      name: f.name,
      slug: f.slug,
      teamMode: f.team_mode,
      fixedTeamId: f.fixed_team_id ?? "",
      selectableTeamIds: f.selectable_team_ids ?? [],
      titleI18n: parseJsonMap(f.title_i18n),
      introI18n: parseJsonMap(f.intro_i18n),
      successI18n: parseJsonMap(f.success_i18n),
      labelsI18n: parseLabelsMap(f.labels_i18n),
      redirectI18n: parseJsonMap(f.redirect_i18n),
      noteI18n: parseJsonMap(f.note_i18n),
      subjectI18n: parseJsonMap(f.subject_i18n),
    });
    setEditingId(f.id);
    setSlugTouched(true);
  };
  const cancel = () => { setEditingId(null); setDraft(EMPTY_FORM_DRAFT); };

  const save = async () => {
    if (!draft.name.trim() || !draft.slug.trim()) return;
    setSaving(true);
    try {
      // Drop empty per-language entries so the JSONB stays compact
      // and pickLang on the backend doesn't trip on empty strings.
      // Iterates over the keys actually present in the draft instead
      // of a hard-coded list of supported languages.
      const compact = (m: I18nMap): I18nMap => {
        const out: I18nMap = {};
        for (const [k, v] of Object.entries(m)) {
          if (typeof v === "string" && v.trim().length > 0 && LANG_CODE_RE.test(k)) {
            out[k] = v.trim();
          }
        }
        return out;
      };
      // Compact the label-override map: drop empty strings, drop
      // empty languages, drop bogus keys. Backend's Zod schema does
      // the same validation but stripping here keeps the JSONB blob
      // small and avoids round-tripping ghost entries through the
      // edit form on a reload.
      const compactLabels = (m: LabelsI18n): LabelsI18n => {
        const out: LabelsI18n = {};
        for (const [langCode, inner] of Object.entries(m)) {
          if (!LANG_CODE_RE.test(langCode) || !inner) continue;
          const cleaned: Partial<Record<LabelOverrideKey, string>> = {};
          for (const k of LABEL_OVERRIDE_KEYS) {
            const v = inner[k];
            if (typeof v === "string" && v.trim().length > 0) cleaned[k] = v.trim();
          }
          if (Object.keys(cleaned).length > 0) out[langCode] = cleaned;
        }
        return out;
      };
      // Redirect URLs: only ship http(s) entries. An empty value
      // means "no redirect for this language" — drop it so the
      // JSONB stays compact. The backend Zod schema enforces the
      // http/https prefix too, but trimming here avoids a 400 the
      // operator could otherwise see for a stray empty cell.
      const compactRedirect = (m: I18nMap): I18nMap => {
        const out: I18nMap = {};
        for (const [k, v] of Object.entries(m)) {
          if (!LANG_CODE_RE.test(k)) continue;
          const trimmed = (v ?? "").trim();
          if (trimmed.length > 0 && /^https?:\/\//i.test(trimmed)) {
            out[k] = trimmed;
          }
        }
        return out;
      };
      const payload = {
        name: draft.name.trim(),
        slug: draft.slug.trim().toLowerCase(),
        teamMode: draft.teamMode,
        fixedTeamId: draft.teamMode === "fixed" ? (draft.fixedTeamId || null) : null,
        selectableTeamIds: draft.teamMode === "choose" ? draft.selectableTeamIds : [],
        titleI18n: compact(draft.titleI18n),
        introI18n: compact(draft.introI18n),
        successI18n: compact(draft.successI18n),
        labelsI18n: compactLabels(draft.labelsI18n),
        redirectI18n: compactRedirect(draft.redirectI18n),
        noteI18n: compact(draft.noteI18n),
        subjectI18n: compact(draft.subjectI18n),
      };
      if (editingId === "new") {
        await api.post("/api/intake-forms", payload);
      } else if (editingId) {
        await api.patch(`/api/intake-forms/${editingId}`, payload);
      }
      cancel();
      await load();
    } catch (err) { onError(err); }
    finally { setSaving(false); }
  };

  const remove = async (id: string) => {
    try {
      await api.del(`/api/intake-forms/${id}`);
      await load();
    } catch (err) { onError(err); }
  };

  const toggleSelectableTeam = (id: string) => {
    setDraft((d) => {
      const has = d.selectableTeamIds.includes(id);
      return {
        ...d,
        selectableTeamIds: has
          ? d.selectableTeamIds.filter((x) => x !== id)
          : [...d.selectableTeamIds, id],
      };
    });
  };

  const renderEditor = () => (
    <div className="card" style={{ background: "var(--surface-alt)" }}>
      <h3 style={{ marginTop: 0 }}>
        {editingId === "new" ? t("admin.forms.newHeading") : t("admin.forms.editHeading")}
      </h3>
      <div className="form-grid form-grid-2">
        <div className="field">
          <label>{t("admin.forms.name")}</label>
          <input
            value={draft.name}
            onChange={(e) => {
              const next = { ...draft, name: e.target.value };
              if (!slugTouched) next.slug = defaultSlug(e.target.value);
              setDraft(next);
            }}
          />
        </div>
        <div className="field">
          <label>{t("admin.forms.slug")}</label>
          <input
            value={draft.slug}
            onChange={(e) => { setDraft({ ...draft, slug: e.target.value }); setSlugTouched(true); }}
          />
          <small style={{ color: "var(--text-muted)" }}>{t("admin.forms.slugHelp")}</small>
        </div>
      </div>
      <div className="field">
        <label>{t("admin.forms.teamMode")}</label>
        <div className="segmented">
          <button
            type="button"
            className={draft.teamMode === "fixed" ? "active" : ""}
            onClick={() => setDraft({ ...draft, teamMode: "fixed" })}
          >
            {t("admin.forms.modeFixed")}
          </button>
          <button
            type="button"
            className={draft.teamMode === "choose" ? "active" : ""}
            onClick={() => setDraft({ ...draft, teamMode: "choose" })}
          >
            {t("admin.forms.modeChoose")}
          </button>
        </div>
      </div>
      {draft.teamMode === "fixed" && (
        <div className="field">
          <label>{t("admin.forms.fixedTeam")}</label>
          <select value={draft.fixedTeamId} onChange={(e) => setDraft({ ...draft, fixedTeamId: e.target.value })}>
            <option value="">{t("tickets.noTeam")}</option>
            {teams.map((tm) => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
          </select>
        </div>
      )}
      {draft.teamMode === "choose" && (
        <div className="field">
          <label>{t("admin.forms.selectableTeams")}</label>
          <small style={{ color: "var(--text-muted)", display: "block", marginBottom: 6 }}>
            {t("admin.forms.selectableTeamsHelp")}
          </small>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {teams.map((tm) => {
              const checked = draft.selectableTeamIds.includes(tm.id);
              return (
                <label key={tm.id} className="inline-checkbox">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleSelectableTeam(tm.id)}
                  />
                  {tm.name}
                </label>
              );
            })}
          </div>
        </div>
      )}
      <I18nFormEditor
        title={draft.titleI18n}
        intro={draft.introI18n}
        success={draft.successI18n}
        labels={draft.labelsI18n}
        redirect={draft.redirectI18n}
        note={draft.noteI18n}
        subject={draft.subjectI18n}
        nameFallback={draft.name}
        onChange={(title, intro, success, labels, redirect, note, subject) => setDraft({
          ...draft,
          titleI18n: title,
          introI18n: intro,
          successI18n: success,
          labelsI18n: labels,
          redirectI18n: redirect,
          noteI18n: note,
          subjectI18n: subject,
        })}
      />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button onClick={cancel} disabled={saving}>{t("common.cancel")}</button>
        <button
          className="primary"
          onClick={save}
          disabled={saving || !draft.name.trim() || !draft.slug.trim()}
        >
          {saving ? t("common.saving") : t("common.save")}
        </button>
      </div>
    </div>
  );

  return (
    <div className="card">
      <div className="toolbar">
        <h3 style={{ margin: 0 }}>{t("admin.forms.heading")}</h3>
        {canWrite && editingId === null && (
          <button onClick={startNew}>{t("admin.forms.newButton")}</button>
        )}
      </div>
      <p style={{ color: "var(--text-muted)", marginTop: 0 }}>{t("admin.forms.intro")}</p>

      {editingId !== null && renderEditor()}

      {forms.map((f) => {
        const fixedTeam = teams.find((tm) => tm.id === f.fixed_team_id);
        return (
          <div key={f.id} className="card" style={{ marginBottom: 10 }}>
            <div className="toolbar">
              <div>
                <h4 style={{ margin: 0 }}>{f.name}</h4>
                <div style={{ color: "var(--text-muted)", fontSize: 13 }}>
                  {f.team_mode === "fixed"
                    ? `${t("admin.forms.modeFixed")} → ${fixedTeam?.name ?? t("tickets.noTeam")}`
                    : t("admin.forms.modeChoose")}
                </div>
              </div>
              {canWrite && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button onClick={() => startEdit(f)}>{t("common.edit")}</button>
                  <ConfirmDeleteButton onDelete={() => remove(f.id)} hint={t("admin.forms.deleteConfirm")} />
                </div>
              )}
            </div>
            {/* One URL + embed per language the operator actually
                configured on this form. The URL hard-pins the
                language. */}
            {(() => {
              const langs = Array.from(new Set([
                ...Object.keys(asI18nMap(f.title_i18n)),
                ...Object.keys(asI18nMap(f.intro_i18n)),
                ...Object.keys(asI18nMap(f.success_i18n)),
              ])).sort();
              if (langs.length === 0) {
                return (
                  <p style={{ color: "var(--text-muted)", fontSize: 13 }}>
                    {t("admin.forms.noLanguages")}
                  </p>
                );
              }
              return langs.map((l) => {
                const url = `${publicBase}/form/${f.slug}/${l}`;
                // Third-party sites cannot iframe app pages (platform
                // CSP: frame-ancestors https://dotbots.boutique). The
                // script snippet renders the form directly into the
                // host page; the customer can restyle .ds-* freely.
                const siteKey = import.meta.env.TURNSTILE_SITE_KEY ?? import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "";
                const backendBase = (import.meta.env.VITE_BACKEND_URL ?? "").replace(/\/$/, "");
                const scriptSrc = `${publicBase}/embed.js`;
                const jsEmbed =
                  `<div data-dotsupport-form="${f.slug}" data-lang="${l}"` +
                  (backendBase ? ` data-backend="${backendBase}"` : "") +
                  (siteKey ? ` data-turnstile-site-key="${siteKey}"` : "") +
                  `></div>\n` +
                  `<script src="${scriptSrc}" async></script>`;
                return (
                  <details key={l} className="form-lang-block">
                    <summary>
                      <strong>{langLabel(l, uiLang)}</strong>
                      <span style={{ color: "var(--text-muted)", fontSize: 12, marginLeft: 8 }}>
                        /form/{f.slug}/{l}
                      </span>
                    </summary>
                    <div className="field">
                      <label>{t("admin.forms.publicUrl")}</label>
                      <div className="copy-row">
                        <code className="url-block">{url}</code>
                        <CopyButton text={url} />
                        <a href={url} target="_blank" rel="noreferrer"><button>{t("admin.forms.open")}</button></a>
                      </div>
                    </div>
                    <div className="field" style={{ marginBottom: 0 }}>
                      <label>{t("admin.forms.embedJs")}</label>
                      <div className="copy-row">
                        <pre className="payload-block">{jsEmbed}</pre>
                        <CopyButton text={jsEmbed} />
                      </div>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6, flexWrap: "wrap" }}>
                        <small style={{ color: "var(--text-muted)" }}>{t("admin.forms.embedJsHelp")}</small>
                        <button
                          type="button"
                          onClick={() => setRestyleOpen(true)}
                          style={{ padding: "4px 10px", fontSize: 12 }}
                        >
                          🎨 {t("admin.forms.embedJsRestyleButton")}
                        </button>
                      </div>
                    </div>
                  </details>
                );
              });
            })()}
          </div>
        );
      })}

      {forms.length === 0 && editingId === null && (
        <p style={{ color: "var(--text-muted)" }}>{t("admin.forms.empty")}</p>
      )}

      {restyleOpen && (
        <EmbedRestyleModal onClose={() => setRestyleOpen(false)} />
      )}
    </div>
  );
}

function EmbedRestyleModal({ onClose }: { onClose: () => void }) {
  const t = useT();
  // ESC closes the modal — mirrors the keyboard story of the shared
  // ModalHost so the two feel consistent.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-panel" style={{ maxWidth: 720 }}>
        <h3 className="modal-title">{t("admin.forms.embedJsRestyleHeading")}</h3>
        <div className="modal-message" style={{ marginBottom: 8 }}>
          {t("admin.forms.embedJsRestyleIntro")}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
          {EMBED_OVERRIDABLE_CLASSES.map((c) => (
            <code
              key={c}
              style={{
                background: "rgba(99, 102, 241, 0.1)",
                color: "#4338ca",
                padding: "2px 6px",
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              {c}
            </code>
          ))}
        </div>
        <div className="modal-message" style={{ marginBottom: 6 }}>
          {t("admin.forms.embedJsRestyleSample")}
        </div>
        <div className="copy-row" style={{ marginBottom: 14 }}>
          <pre
            className="payload-block"
            style={{ maxHeight: 320, overflow: "auto", margin: 0 }}
          >{EMBED_RESTYLE_SAMPLE_CSS}</pre>
          <CopyButton text={EMBED_RESTYLE_SAMPLE_CSS} />
        </div>
        <div className="modal-actions">
          <button type="button" className="primary" onClick={onClose}>
            {t("common.close")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Bulk reassign (admin) ----------

function BulkReassignCard({
  agents,
  canWrite,
  onError,
  onDone,
}: {
  agents: Agent[];
  canWrite: boolean;
  onError: (err: unknown) => void;
  onDone: (count: number) => void;
}) {
  const api = useApi();
  const t = useT();
  // source value: "" = all private, otherwise an agent id (= "all
  // tickets currently assigned to that agent").
  const [source, setSource] = useState<string>("");
  const [onlyPrivate, setOnlyPrivate] = useState(false);
  const [target, setTarget] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  if (!canWrite) return null;

  const sourceIsAgent = source !== "";
  const canSubmit = !!target && (sourceIsAgent || onlyPrivate || source === "");
  const apply = async () => {
    if (!target) return;
    if (!sourceIsAgent && !onlyPrivate) {
      // "All open tickets" without any filter would be a footgun; the
      // backend rejects it too, but stop the call here for clarity.
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post<{ updated: number }>("/api/tickets/bulk/reassign", {
        toAssigneeId: target,
        fromAssigneeId: sourceIsAgent ? source : undefined,
        onlyPrivate: sourceIsAgent ? onlyPrivate : true,
      });
      onDone(res.updated);
      setSource("");
      setOnlyPrivate(false);
      setTarget("");
    } catch (err) {
      onError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>{t("admin.bulk.heading")}</h3>
      <p style={{ color: "var(--text-muted)", marginTop: 0 }}>{t("admin.bulk.intro")}</p>

      <div className="form-grid form-grid-2">
        <div className="field">
          <label>{t("admin.bulk.source")}</label>
          <select value={source} onChange={(e) => setSource(e.target.value)} disabled={submitting}>
            <option value="">{t("admin.bulk.allPrivate")}</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {t("admin.bulk.fromAgent", { name: a.name })}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>{t("admin.bulk.target")}</label>
          <select value={target} onChange={(e) => setTarget(e.target.value)} disabled={submitting}>
            <option value="">{t("admin.private.pickAgent")}</option>
            {agents.filter((a) => !sourceIsAgent || a.id !== source).map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>
      </div>
      {sourceIsAgent && (
        <div className="field">
          <label className="inline-checkbox">
            <input
              type="checkbox"
              checked={onlyPrivate}
              onChange={(e) => setOnlyPrivate(e.target.checked)}
              disabled={submitting}
            />
            {t("admin.bulk.onlyPrivate")}
          </label>
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button
          className="primary"
          onClick={apply}
          disabled={!canSubmit || submitting}
        >
          {submitting ? t("admin.bulk.applying") : t("admin.bulk.apply")}
        </button>
      </div>
    </div>
  );
}

// Multi-language editor for an intake form's intro and success text.
// One language tab strip drives both textareas, so the operator
// types e.g. "Spanish" once and sees the Spanish intro + the Spanish
// success in the same view. Languages are fully dynamic — operators
// add or remove any IETF code (zh, es, pt-BR, …) and AI-translate
// the active language's content into every other added language.
function I18nFormEditor({
  title,
  intro,
  success,
  labels,
  redirect,
  note,
  subject,
  nameFallback,
  onChange,
}: {
  title: I18nMap;
  intro: I18nMap;
  success: I18nMap;
  labels: LabelsI18n;
  redirect: I18nMap;
  note: I18nMap;
  subject: I18nMap;
  // The admin-only `name` field — only used here as a placeholder hint
  // in the Title input so the operator can see what would be shown
  // today (the name) when no per-language title is configured yet.
  nameFallback: string;
  onChange: (
    title: I18nMap,
    intro: I18nMap,
    success: I18nMap,
    labels: LabelsI18n,
    redirect: I18nMap,
    note: I18nMap,
    subject: I18nMap,
  ) => void;
}) {
  const api = useApi();
  const t = useT();
  const { lang: uiLang } = useI18n();
  const [labelsOpen, setLabelsOpen] = useState(false);

  const langs = useMemo(() => {
    const set = new Set<string>([
      ...Object.keys(title),
      ...Object.keys(intro),
      ...Object.keys(success),
      ...Object.keys(labels),
      ...Object.keys(redirect),
    ]);
    return Array.from(set).sort();
  }, [title, intro, success, labels, redirect]);

  // Pick the first reasonable language to land on: operator's UI lang
  // if configured, else English, else the first available.
  const initialActive = langs.includes(uiLang)
    ? uiLang
    : (langs.includes("en") ? "en" : (langs[0] ?? ""));
  const [active, setActive] = useState<string>(initialActive);
  const [adding, setAdding] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [translating, setTranslating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If the active language disappears (operator removed it elsewhere)
  // re-anchor on a sensible default.
  useEffect(() => {
    if (active && langs.includes(active)) return;
    setActive(initialActive);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langs.join("|")]);

  const addLang = (raw: string) => {
    const code = raw.trim().toLowerCase();
    if (!LANG_CODE_RE.test(code)) {
      setError(t("admin.forms.invalidLangCode"));
      return;
    }
    setError(null);
    if (title[code] !== undefined || intro[code] !== undefined || success[code] !== undefined) {
      setActive(code);
      setAdding(false);
      setNewCode("");
      return;
    }
    onChange(
      { ...title, [code]: "" },
      { ...intro, [code]: "" },
      { ...success, [code]: "" },
      labels,
      redirect,
      { ...note, [code]: "" },
      { ...subject, [code]: "" },
    );
    setActive(code);
    setAdding(false);
    setNewCode("");
  };

  const removeLang = (code: string) => {
    const ti = { ...title }; delete ti[code];
    const i = { ...intro }; delete i[code];
    const s = { ...success }; delete s[code];
    const lb = { ...labels }; delete lb[code];
    const r = { ...redirect }; delete r[code];
    const n = { ...note }; delete n[code];
    const sub = { ...subject }; delete sub[code];
    onChange(ti, i, s, lb, r, n, sub);
  };

  // Pick the best filled language to use as a translation source for
  // the active tab — operator's UI lang first (most likely the one
  // they actually wrote), then English, then any other filled lang.
  // A language counts as filled if any of title/intro/success has a
  // non-empty value.
  const bestSourceFor = (target: string): string | null => {
    const filled = langs.filter((l) =>
      l !== target &&
      (
        (title[l]?.trim() || "").length > 0 ||
        (intro[l]?.trim() || "").length > 0 ||
        (success[l]?.trim() || "").length > 0
      )
    );
    if (filled.length === 0) return null;
    if (filled.includes(uiLang)) return uiLang;
    if (filled.includes("en")) return "en";
    return filled[0];
  };

  const fillActiveFromAnother = async () => {
    const source = bestSourceFor(active);
    if (!source) {
      setError(t("admin.forms.translateNoSource"));
      return;
    }
    const sourceTitle = (title[source] ?? "").trim();
    const sourceIntro = (intro[source] ?? "").trim();
    const sourceSuccess = (success[source] ?? "").trim();
    setTranslating(true);
    setError(null);
    try {
      const newTitle = { ...title };
      const newIntro = { ...intro };
      const newSuccess = { ...success };
      const tasks: Promise<unknown>[] = [];
      if (sourceTitle) {
        tasks.push(
          api.post<{ translation: string }>("/api/ai/translate", {
            text: sourceTitle, targetLanguage: active,
          }).then((r) => { newTitle[active] = r.translation; }),
        );
      }
      if (sourceIntro) {
        tasks.push(
          api.post<{ translation: string }>("/api/ai/translate", {
            text: sourceIntro, targetLanguage: active,
          }).then((r) => { newIntro[active] = r.translation; }),
        );
      }
      if (sourceSuccess) {
        tasks.push(
          api.post<{ translation: string }>("/api/ai/translate", {
            text: sourceSuccess, targetLanguage: active,
          }).then((r) => { newSuccess[active] = r.translation; }),
        );
      }
      await Promise.all(tasks);
      onChange(newTitle, newIntro, newSuccess, labels, redirect, note, subject);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setTranslating(false);
    }
  };

  const translateFromActive = async () => {
    const sourceTitle = (title[active] ?? "").trim();
    const sourceIntro = (intro[active] ?? "").trim();
    const sourceSuccess = (success[active] ?? "").trim();
    if (!sourceTitle && !sourceIntro && !sourceSuccess) {
      setError(t("admin.forms.translateEmpty"));
      return;
    }
    const targets = langs.filter((l) => l !== active);
    if (targets.length === 0) {
      setError(t("admin.forms.translateNoTargets"));
      return;
    }
    setTranslating(true);
    setError(null);
    try {
      const newTitle = { ...title };
      const newIntro = { ...intro };
      const newSuccess = { ...success };
      const tasks: Promise<unknown>[] = [];
      for (const target of targets) {
        if (sourceTitle) {
          tasks.push(
            api.post<{ translation: string }>("/api/ai/translate", {
              text: sourceTitle, targetLanguage: target,
            }).then((r) => { newTitle[target] = r.translation; }),
          );
        }
        if (sourceIntro) {
          tasks.push(
            api.post<{ translation: string }>("/api/ai/translate", {
              text: sourceIntro, targetLanguage: target,
            }).then((r) => { newIntro[target] = r.translation; }),
          );
        }
        if (sourceSuccess) {
          tasks.push(
            api.post<{ translation: string }>("/api/ai/translate", {
              text: sourceSuccess, targetLanguage: target,
            }).then((r) => { newSuccess[target] = r.translation; }),
          );
        }
      }
      await Promise.all(tasks);
      onChange(newTitle, newIntro, newSuccess, labels, redirect, note, subject);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="i18n-form-editor field">
      <label>{t("admin.forms.translations")}</label>
      <div className="i18n-tab-strip" role="tablist">
        {langs.map((l) => {
          const filled =
            (title[l]?.trim() || "") ||
            (intro[l]?.trim() || "") ||
            (success[l]?.trim() || "");
          return (
            <span key={l} className={`i18n-tab ${active === l ? "active" : ""}`}>
              <button
                type="button"
                role="tab"
                aria-selected={active === l}
                onClick={() => setActive(l)}
              >
                {langLabel(l, uiLang)}{filled ? "" : " ◯"}
              </button>
              <button
                type="button"
                className="i18n-tab-x"
                aria-label={t("common.delete")}
                onClick={() => removeLang(l)}
              >
                ✕
              </button>
            </span>
          );
        })}
        <button
          type="button"
          className="i18n-tab-add"
          onClick={() => { setAdding((s) => !s); setError(null); }}
        >
          + {t("admin.forms.addLanguage")}
        </button>
      </div>

      {adding && (
        <div className="i18n-add-panel">
          <select
            value=""
            onChange={(e) => { if (e.target.value) addLang(e.target.value); }}
          >
            <option value="">{t("admin.forms.pickLanguage")}</option>
            {COMMON_LANGS
              .filter((c) => title[c] === undefined && intro[c] === undefined && success[c] === undefined)
              .map((c) => (
                <option key={c} value={c}>{langLabel(c, uiLang)} — {c}</option>
              ))}
          </select>
          <input
            placeholder={t("admin.forms.customLangCode")}
            value={newCode}
            onChange={(e) => setNewCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); addLang(newCode); }
            }}
          />
          <button type="button" className="primary" onClick={() => addLang(newCode)} disabled={!newCode.trim()}>
            {t("common.add")}
          </button>
          <button type="button" onClick={() => { setAdding(false); setNewCode(""); setError(null); }}>
            {t("common.cancel")}
          </button>
        </div>
      )}

      {!langs.length && (
        <p style={{ color: "var(--text-muted)", marginTop: 8 }}>{t("admin.forms.noLanguages")}</p>
      )}

      {active && (
        <>
          <div className="field" style={{ marginTop: 12 }}>
            <label>{t("admin.forms.titleField")}</label>
            <input
              value={title[active] ?? ""}
              placeholder={nameFallback || t("admin.forms.titlePlaceholder")}
              onChange={(e) => onChange({ ...title, [active]: e.target.value }, intro, success, labels, redirect, note, subject)}
            />
            <small style={{ color: "var(--text-muted)" }}>{t("admin.forms.titleHelp")}</small>
          </div>
          <div className="field">
            <label>{t("admin.forms.introText")}</label>
            <textarea
              value={intro[active] ?? ""}
              onChange={(e) => onChange(title, { ...intro, [active]: e.target.value }, success, labels, redirect, note, subject)}
            />
          </div>
          <div className="field">
            <label>{t("admin.forms.successText")}</label>
            <textarea
              value={success[active] ?? ""}
              onChange={(e) => onChange(title, intro, { ...success, [active]: e.target.value }, labels, redirect, note, subject)}
            />
          </div>

          {/* Optional post-submit redirect. Empty = show the success
              text (default behaviour); a URL means jump there after
              the ticket is created. Validation here is pragmatic
              (just a heads-up on missing http(s) prefix); the
              backend Zod schema does the strict check. */}
          <div className="field">
            <label>{t("admin.forms.redirectUrl")}</label>
            <input
              type="url"
              value={redirect[active] ?? ""}
              placeholder={t("admin.forms.redirectUrlPlaceholder")}
              onChange={(e) => onChange(
                title,
                intro,
                success,
                labels,
                { ...redirect, [active]: e.target.value },
                note,
                subject,
              )}
            />
            <small style={{ color: "var(--text-muted)" }}>{t("admin.forms.redirectUrlHelp")}</small>
          </div>

          {/* Optional fixed subject. Non-empty here hides the subject
              input on the public form and the backend overrides any
              client-sent value with this string. */}
          <div className="field">
            <label>{t("admin.forms.fixedSubject")}</label>
            <input
              value={subject[active] ?? ""}
              placeholder={t("admin.forms.fixedSubjectPlaceholder")}
              onChange={(e) => onChange(
                title,
                intro,
                success,
                labels,
                redirect,
                note,
                { ...subject, [active]: e.target.value },
              )}
            />
            <small style={{ color: "var(--text-muted)" }}>{t("admin.forms.fixedSubjectHelp")}</small>
          </div>

          {/* Small print rendered under the form (privacy, branding,
              legal). HTML allowed — operators commonly paste a <br>
              + an <a href> snippet. */}
          <div className="field">
            <label>{t("admin.forms.noteHtml")}</label>
            <textarea
              value={note[active] ?? ""}
              placeholder={t("admin.forms.noteHtmlPlaceholder")}
              onChange={(e) => onChange(
                title,
                intro,
                success,
                labels,
                redirect,
                { ...note, [active]: e.target.value },
                subject,
              )}
            />
            <small style={{ color: "var(--text-muted)" }}>{t("admin.forms.noteHtmlHelp")}</small>
          </div>

          {/* Advanced: per-language overrides for the visitor-facing
              field labels and submit button caption. Collapsed by
              default so the common case (just title/intro/success)
              stays uncluttered. Empty inputs fall back to the
              AI-translated defaults; placeholders show the canonical
              English label so the operator sees what they're
              replacing. */}
          <div className="field" style={{ marginBottom: 0 }}>
            <button
              type="button"
              onClick={() => setLabelsOpen((v) => !v)}
              style={{ fontSize: 12 }}
              aria-expanded={labelsOpen}
            >
              {labelsOpen
                ? t("admin.forms.fieldLabelsHide")
                : t("admin.forms.fieldLabelsShow")}
            </button>
            <small style={{ color: "var(--text-muted)", display: "block", marginTop: 4 }}>
              {t("admin.forms.fieldLabelsHelp")}
            </small>
          </div>
          {labelsOpen && (
            <div
              style={{
                marginTop: 6,
                padding: 12,
                border: "1px solid var(--border)",
                borderRadius: 6,
                background: "var(--surface-alt, rgba(0,0,0,0.02))",
              }}
            >
              {LABEL_OVERRIDE_KEYS.map((key) => {
                const value = labels[active]?.[key] ?? "";
                const placeholder = t(`admin.forms.fieldLabel.${key}.placeholder`);
                const update = (next: string) => {
                  const langEntry: Partial<Record<LabelOverrideKey, string>> = {
                    ...(labels[active] ?? {}),
                  };
                  if (next.length === 0) delete langEntry[key];
                  else langEntry[key] = next;
                  const nextLabels: LabelsI18n = { ...labels };
                  if (Object.keys(langEntry).length === 0) delete nextLabels[active];
                  else nextLabels[active] = langEntry;
                  onChange(title, intro, success, nextLabels, redirect, note, subject);
                };
                return (
                  <div key={key} className="field" style={{ marginBottom: 8 }}>
                    <label>{t(`admin.forms.fieldLabel.${key}`)}</label>
                    <input
                      value={value}
                      placeholder={placeholder}
                      onChange={(e) => update(e.target.value)}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {error && <div style={{ color: "var(--danger)", marginBottom: 8 }}>{error}</div>}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {/* Fill ONLY the active language from the best filled
                source — for operators who already wrote one language
                and just want to add another translation without
                fanning out to every other tab. */}
            {(() => {
              const source = bestSourceFor(active);
              return (
                <button
                  type="button"
                  onClick={fillActiveFromAnother}
                  disabled={translating || !source}
                  title={source
                    ? t("admin.forms.fillFromHint", { from: langLabel(source, uiLang), target: langLabel(active, uiLang) })
                    : t("admin.forms.translateNoSource")}
                >
                  {translating
                    ? t("admin.forms.translating")
                    : (source
                      ? t("admin.forms.fillFrom", { from: langLabel(source, uiLang), target: langLabel(active, uiLang) })
                      : t("admin.forms.fillFromDisabled", { target: langLabel(active, uiLang) }))}
                </button>
              );
            })()}
            <button
              type="button"
              onClick={translateFromActive}
              disabled={translating || langs.length < 2}
              title={t("admin.forms.translateAllHint", { from: langLabel(active, uiLang) })}
            >
              {translating
                ? t("admin.forms.translating")
                : t("admin.forms.translateAll", { from: langLabel(active, uiLang) })}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
